import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Icon,
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2FormField,
  V2FormGrid,
  V2Modal,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { ApiError, apiPatch, apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { PartyLink } from './PartyLink.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { useAmountDisplay, useTenantSettings } from '../../lib/tenantSettings.js';
import { vatEnabled } from '../../lib/types/tenantSettings.js';
import type { CatalogListResponse, MasterDataListResponse } from '../../lib/types/masterData.js';
import type { Quote, QuoteFlow, QuoteTransitionTarget } from '../../lib/types/crm.js';
import {
  QuoteLinesEditor,
  draftsFromLines,
  payloadLines,
  type QuoteLineDraft,
} from './QuoteLinesEditor.js';
import { QuoteFlowPanel } from './QuoteFlowPanel.js';

const STATUS_TONE: Record<string, V2BadgeTone> = {
  پیش‌نویس: 'neutral',
  ارسال‌شده: 'info',
  پذیرفته‌شده: 'success',
  'رد شده': 'danger',
  منقضی: 'neutral',
};

const TRANSITION_LABEL: Record<QuoteTransitionTarget, string> = {
  sent: 'ثبت ارسال به مشتری',
  accepted: 'ثبت پذیرش مشتری',
  rejected: 'ثبت رد مشتری',
};

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const first = err.envelope.error.details?.[0]?.message;
    return first ?? err.envelope.error.message;
  }
  return fallback;
}

/**
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — `/t/kimia/sales` (detail screens use the
 *   module header + V2Card sections); Sakhtar Sales CRM.dc.html §05 QUOTES — detail.
 * status: KIT_CONFORMANT, V2-only.
 *
 * One page for the whole life of a pre-invoice: edit (while draft/sent), send / accept /
 * reject, the printable HTML document, conversion to a sales order with editable lines,
 * and the path from there to finance (order → delivery → invoice → journal + receivable).
 * Every number is the server's; permissions are enforced by the API (buttons only hide).
 * state reference: ready · permission_denied · not_found · network_error
 */
export function QuoteDetailPage() {
  const { unit, label } = useAmountDisplay();
  const { slug, ref } = useParams<{ slug: string; ref: string }>();
  const navigate = useNavigate();
  const base = slug ? tenantBasePath(slug) : '';
  const showVat = vatEnabled(useTenantSettings());
  const [refreshKey, setRefreshKey] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<'edit' | 'convert' | null>(null);

  const quote = useApiGet<Quote>(slug && ref ? `${base}/api/v1/sales/quotes/${ref}` : null, [
    slug,
    ref,
    refreshKey,
  ]);
  const flow = useApiGet<QuoteFlow>(
    slug && ref ? `${base}/api/v1/sales/quotes/${ref}/flow` : null,
    [slug, ref, refreshKey],
  );

  if (!slug || !ref) return null;

  async function runTransition(target: QuoteTransitionTarget) {
    setBusy(true);
    setActionError(null);
    try {
      const path = target === 'sent' ? 'submit' : target === 'accepted' ? 'accept' : 'reject';
      await apiPost(`${base}/api/v1/sales/quotes/${ref}/${path}`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setActionError(errorText(err, 'ثبت اقدام ناموفق بود.'));
    } finally {
      setBusy(false);
    }
  }

  const liveOrder =
    flow.status === 'ready' && flow.data.orders.status === 'ready'
      ? flow.data.orders.items.find((o) => o.status !== 'cancelled')
      : undefined;
  const editable =
    quote.status === 'ready' &&
    (quote.data.status === 'draft' || quote.data.status === 'sent') &&
    liveOrder === undefined;
  const convertible =
    quote.status === 'ready' &&
    (quote.data.status === 'sent' || quote.data.status === 'accepted') &&
    liveOrder === undefined &&
    flow.status === 'ready';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="sk-breadcrumb">
        <span>فروش</span>
        <span>/</span>
        <Link to={`${base}/crm/quotes`}>پیش‌فاکتورها</Link>
        <span>/</span>
        <b className="ltr" style={{ fontFamily: 'var(--sk-font-mono)' }}>
          {ref}
        </b>
      </div>

      {quote.status === 'loading' ? <LoadingState /> : null}
      {quote.status === 'error' ? (
        <StatePanel
          variant={
            quote.error.status === 403
              ? 'permission_denied'
              : quote.error.status === 404
                ? 'not_found'
                : 'network_error'
          }
          title={
            quote.error.status === 403
              ? 'دسترسی ندارید'
              : quote.error.status === 404
                ? 'پیدا نشد'
                : 'قابل نمایش نیست'
          }
        />
      ) : null}

      {quote.status === 'ready' ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>پیش‌فاکتور</h1>
            <V2Badge tone={STATUS_TONE[quote.data.statusLabel] ?? 'neutral'}>
              {quote.data.statusLabel}
            </V2Badge>
            <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Link to={`${base}/crm/quotes/${ref}/print`} className="sk-btn sk-btn--secondary">
                <Icon name="invoice" size="sm" />
                خروجی HTML / چاپ
              </Link>
              {editable ? (
                <V2Button variant="secondary" icon="settings" onClick={() => setDialog('edit')}>
                  ویرایش
                </V2Button>
              ) : null}
              {quote.data.allowedTransitions.map((target) => (
                <V2Button
                  key={target}
                  variant={target === 'rejected' ? 'ghost' : 'secondary'}
                  disabled={busy}
                  onClick={() => void runTransition(target)}
                >
                  {TRANSITION_LABEL[target]}
                </V2Button>
              ))}
              {convertible ? (
                <V2Button variant="primary" icon="cart" onClick={() => setDialog('convert')}>
                  تبدیل به سفارش / فاکتور
                </V2Button>
              ) : null}
            </div>
          </div>

          {actionError ? <V2Alert tone="danger">{actionError}</V2Alert> : null}

          <V2Card>
            <div
              style={{
                padding: '16px 20px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 16,
              }}
            >
              <Meta
                title="طرف‌حساب"
                value={
                  quote.data.partyRef ? (
                    <PartyLink base={base} partyRef={quote.data.partyRef}>
                      {quote.data.partyRef}
                    </PartyLink>
                  ) : (
                    '—'
                  )
                }
              />
              <Meta title="سرنخ مرتبط" value={quote.data.leadRef ?? '—'} />
              <Meta
                title="مبلغ کل"
                value={`${formatRialFa(quote.data.totalRial, unit)} ${label}`}
                numeric
              />
              <Meta title="اعتبار تا" value={displayJalaliDate(quote.data.validUntil)} />
            </div>
          </V2Card>

          {flow.status === 'loading' ? <LoadingState /> : null}
          {flow.status === 'error' ? (
            <V2Alert tone="warn">مسیر پیش‌فاکتور تا مالی در دسترس نیست.</V2Alert>
          ) : null}
          {flow.status === 'ready' ? <QuoteFlowPanel flow={flow.data} base={base} /> : null}

          {quote.data.lines && quote.data.lines.length > 0 ? (
            <V2Card>
              <V2CardHeader title="اقلام" icon="invoice" />
              <div className="sk-table-scroll">
                <table className="sk-table sk-table--compact">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>شرح کالا</th>
                      <th>کد کالا</th>
                      <th>مقدار</th>
                      <th>واحد</th>
                      <th>فی ({label})</th>
                      <th>تخفیف ({label})</th>
                      <th>مالیات</th>
                      <th>جمع ردیف ({label})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.data.lines.map((line) => (
                      <tr key={line.rowNumber}>
                        <td>{line.rowNumber.toLocaleString('fa-IR')}</td>
                        <td>{line.description}</td>
                        <td className="ltr sk-num" style={{ fontFamily: 'var(--sk-font-mono)' }}>
                          {line.productRef}
                        </td>
                        <td>{line.quantity}</td>
                        <td>{line.uomRef}</td>
                        <td className="ltr sk-num">{formatRialFa(line.unitPriceRial, unit)}</td>
                        <td className="ltr sk-num">{formatRialFa(line.discountRial, unit)}</td>
                        <td className="ltr sk-num">
                          {line.taxRef ?? '—'}
                          {line.taxAmountRial !== '0'
                            ? ` (${formatRialFa(line.taxAmountRial, unit)})`
                            : ''}
                        </td>
                        <td className="ltr sk-num">{formatRialFa(line.lineTotalRial, unit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </V2Card>
          ) : null}

          {dialog === 'edit' ? (
            <QuoteEditDialog
              quote={quote.data}
              base={base}
              showVat={showVat}
              onClose={() => setDialog(null)}
              onSaved={() => {
                setDialog(null);
                setRefreshKey((k) => k + 1);
              }}
            />
          ) : null}
          {dialog === 'convert' ? (
            <QuoteConvertDialog
              quote={quote.data}
              base={base}
              showVat={showVat}
              onClose={() => setDialog(null)}
              onConverted={(orderRef) => {
                setDialog(null);
                navigate(`${base}/crm/orders/${encodeURIComponent(orderRef)}`);
              }}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function Meta({ title, value, numeric }: { title: string; value: ReactNode; numeric?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>{title}</span>
      <span
        className={numeric ? 'ltr sk-num' : undefined}
        style={{ fontSize: 14, fontWeight: 500 }}
      >
        {value}
      </span>
    </div>
  );
}

/** Product / UoM / tax pick-lists shared by both dialogs. */
function useLineOptions(base: string) {
  const products = useApiGet<CatalogListResponse>(
    `${base}/api/v1/master-data/product-catalog?status=active&limit=200`,
    [base],
  );
  const taxes = useApiGet<MasterDataListResponse>(
    `${base}/api/v1/master-data/taxes?status=active&limit=200`,
    [base],
  );
  return {
    ready: products.status === 'ready',
    products: products.status === 'ready' ? products.data.items : [],
    taxes: taxes.status === 'ready' ? taxes.data.items : [],
  };
}

function QuoteEditDialog({
  quote,
  base,
  showVat,
  onClose,
  onSaved,
}: {
  quote: Quote;
  base: string;
  showVat: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const options = useLineOptions(base);
  const parties = useApiGet<MasterDataListResponse>(
    `${base}/api/v1/master-data/parties?role=customer&limit=200`,
    [base],
  );
  const [partyRef, setPartyRef] = useState(quote.partyRef ?? '');
  const [validUntil, setValidUntil] = useState(quote.validUntil ?? '');
  const [lines, setLines] = useState<QuoteLineDraft[]>(() => draftsFromLines(quote.lines));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    const payload = payloadLines(lines);
    if (payload.length === 0) {
      setError('حداقل یک قلم با کالا، مقدار و واحد لازم است.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiPatch(`${base}/api/v1/sales/quotes/${quote.ref}`, {
        partyRef: partyRef || undefined,
        validUntil,
        lines: payload,
      });
      onSaved();
    } catch (err) {
      setError(errorText(err, 'ذخیرهٔ ویرایش ناموفق بود.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <V2Modal
      open
      size="lg"
      title={`ویرایش پیش‌فاکتور ${quote.ref}`}
      onClose={onClose}
      dismissible={!saving}
      footer={
        <>
          <V2Button variant="ghost" onClick={onClose} disabled={saving}>
            انصراف
          </V2Button>
          <V2Button
            variant="primary"
            onClick={() => void save()}
            disabled={saving || !options.ready}
          >
            {saving ? 'در حال ذخیره…' : 'ذخیرهٔ تغییرات'}
          </V2Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {quote.status === 'sent' ? (
          <V2Alert tone="info">
            این پیش‌فاکتور ارسال شده است؛ پس از ذخیرهٔ تغییرات دوباره به «پیش‌نویس» برمی‌گردد و باید
            دوباره برای مشتری ارسال شود.
          </V2Alert>
        ) : null}
        {error ? <V2Alert tone="danger">{error}</V2Alert> : null}
        <V2FormGrid>
          <V2FormField label="طرف‌حساب" width="wide">
            <select
              className="sk-select"
              value={partyRef}
              onChange={(e) => setPartyRef(e.target.value)}
              disabled={parties.status !== 'ready'}
            >
              <option value="">— بدون تغییر —</option>
              {parties.status === 'ready'
                ? parties.data.items.map((p) => (
                    <option key={String(p.code)} value={String(p.code)}>
                      {String(p.code)} — {String(p.name ?? p.code)}
                    </option>
                  ))
                : null}
            </select>
          </V2FormField>
          <V2FormField label="اعتبار تا" width="medium">
            <JalaliDateInput value={validUntil} onChange={setValidUntil} />
          </V2FormField>
        </V2FormGrid>
        <QuoteLinesEditor
          base={base}
          lines={lines}
          onChange={setLines}
          products={options.products}
          taxes={options.taxes}
          showVat={showVat}
          unitLabel="ریال"
        />
      </div>
    </V2Modal>
  );
}

function QuoteConvertDialog({
  quote,
  base,
  showVat,
  onClose,
  onConverted,
}: {
  quote: Quote;
  base: string;
  showVat: boolean;
  onClose: () => void;
  onConverted: (orderRef: string) => void;
}) {
  const options = useLineOptions(base);
  const original = useMemo(() => draftsFromLines(quote.lines), [quote.lines]);
  const [lines, setLines] = useState<QuoteLineDraft[]>(original);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => setLines(original), [original]);

  const edited = JSON.stringify(lines) !== JSON.stringify(original);

  async function convert() {
    const payload = payloadLines(lines);
    if (payload.length === 0) {
      setError('حداقل یک قلم با کالا، مقدار و واحد لازم است.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const order = await apiPost<{ ref: string }>(
        `${base}/api/v1/sales/quotes/${quote.ref}/convert`,
        edited ? { lines: payload } : {},
      );
      onConverted(order.ref);
    } catch (err) {
      setError(errorText(err, 'تبدیل به سفارش ناموفق بود.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <V2Modal
      open
      size="lg"
      title={`تبدیل ${quote.ref} به سفارش فروش`}
      onClose={onClose}
      dismissible={!saving}
      footer={
        <>
          <V2Button variant="ghost" onClick={onClose} disabled={saving}>
            انصراف
          </V2Button>
          <V2Button
            variant="primary"
            onClick={() => void convert()}
            disabled={saving || !options.ready}
          >
            {saving ? 'در حال ثبت…' : edited ? 'ثبت سفارش با اقلام ویرایش‌شده' : 'ثبت سفارش'}
          </V2Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <V2Alert tone="info" title="فاکتور چگونه صادر می‌شود؟">
          سفارش از روی اقلام زیر ساخته می‌شود و می‌توانید پیش از ثبت، مقدار و قیمت را اصلاح کنید
          (پیش‌فاکتور اصلی بدون تغییر می‌ماند). فاکتور فروش پس از رزرو و تحویل کالا از صفحهٔ سفارش
          صادر می‌شود و همان‌جا سند درآمد (پیش‌نویس) و مطالبهٔ مشتری در مالی ساخته می‌شود.
        </V2Alert>
        {quote.status === 'sent' ? (
          <V2Alert tone="warn">با ثبت سفارش، این پیش‌فاکتور «پذیرفته‌شده» ثبت می‌شود.</V2Alert>
        ) : null}
        {error ? <V2Alert tone="danger">{error}</V2Alert> : null}
        <QuoteLinesEditor
          base={base}
          lines={lines}
          onChange={setLines}
          products={options.products}
          taxes={options.taxes}
          showVat={showVat}
          unitLabel="ریال"
        />
      </div>
    </V2Modal>
  );
}
